import { useEffect, useCallback, useRef } from 'react';

const useCacheOptimization = (cacheManager) => {
  const backgroundRefreshInterval = useRef(null);
  const isRefreshing = useRef(false);

  // Background refresh for critical AI insights
  const scheduleBackgroundRefresh = useCallback(async () => {
    if (isRefreshing.current) return;
    
    isRefreshing.current = true;
    
    try {
      // Check if AI insights are stale (older than 1.5 hours)
      const cacheInfo = await cacheManager.getCacheInfo();
      const staleThreshold = 1.5 * 60 * 60 * 1000; // 1.5 hours
      
      // Look for AI insights that need refreshing
      const aiCacheKeys = cacheInfo.memory?.keys?.filter(key => 
        key.includes('ai-insights')
      ) || [];

      for (const key of aiCacheKeys) {
        try {
          // Extract endpoint and options from cache key
          const [endpoint, queryString] = key.split('?');
          const query = queryString ? JSON.parse(queryString) : {};
          
          // Check if this cache entry is stale
          const response = await fetch('/api/bugs/analytics/cache', {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'X-API-KEY': process.env.REACT_APP_API_KEY || ''
            },
            query: new URLSearchParams({ key })
          });
          
          if (response.ok) {
            const cached = await response.json();
            const age = Date.now() - new Date(cached.createdAt).getTime();
            
            if (age > staleThreshold) {
              console.log('Background refresh: Updating stale AI cache for', endpoint);
              // Trigger a background refresh
              await cacheManager.makeRequest(endpoint, { query, background: true });
            }
          }
        } catch (error) {
          console.warn('Background refresh failed for key:', key, error);
        }
      }
    } catch (error) {
      console.warn('Background cache optimization failed:', error);
    } finally {
      isRefreshing.current = false;
    }
  }, [cacheManager]);

  // Preload frequently accessed endpoints
  const preloadCriticalData = useCallback(async () => {
    const criticalEndpoints = [
      '/api/bugs/analytics/dashboard',
      '/api/bugs/analytics/insights'
    ];

    for (const endpoint of criticalEndpoints) {
      try {
        await cacheManager.makeRequest(endpoint, { background: true });
      } catch (error) {
        console.warn('Preload failed for', endpoint, error);
      }
    }
  }, [cacheManager]);

  // Smart cache warming based on user patterns
  const warmCache = useCallback(async () => {
    // Get user's recent access patterns (simplified)
    const recentPatterns = JSON.parse(localStorage.getItem('analyticsAccessPatterns') || '[]');
    
    // Warm cache for frequently accessed combinations
    const frequentPatterns = recentPatterns
      .reduce((acc, pattern) => {
        const key = `${pattern.endpoint}?${JSON.stringify(pattern.query || {})}`;
        acc[key] = (acc[key] || 0) + 1;
        return acc;
      }, {});

    // Pre-fetch top 3 most frequent patterns
    const topPatterns = Object.entries(frequentPatterns)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 3);

    for (const [key] of topPatterns) {
      try {
        const [endpoint, queryString] = key.split('?');
        const query = queryString ? JSON.parse(queryString) : {};
        await cacheManager.makeRequest(endpoint, { query, background: true });
      } catch (error) {
        console.warn('Cache warming failed for pattern:', key, error);
      }
    }
  }, [cacheManager]);

  // Track access patterns for future optimization
  const trackAccess = useCallback((endpoint, query = {}) => {
    const patterns = JSON.parse(localStorage.getItem('analyticsAccessPatterns') || '[]');
    patterns.push({
      endpoint,
      query,
      timestamp: Date.now()
    });
    
    // Keep only last 100 patterns
    const recentPatterns = patterns.slice(-100);
    localStorage.setItem('analyticsAccessPatterns', JSON.stringify(recentPatterns));
  }, []);

  // Cleanup old cache entries
  const cleanupOldCache = useCallback(async () => {
    try {
      // This would typically be handled by the backend, but we can trigger cleanup
      await fetch('/api/bugs/analytics/cache/cleanup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-KEY': process.env.REACT_APP_API_KEY || ''
        }
      });
    } catch (error) {
      console.warn('Cache cleanup failed:', error);
    }
  }, []);

  // Initialize optimization routines
  useEffect(() => {
    // Start background optimization after initial load
    const initTimer = setTimeout(() => {
      preloadCriticalData();
      warmCache();
    }, 5000); // Wait 5 seconds after mount

    // Schedule periodic background refresh (every 30 minutes)
    backgroundRefreshInterval.current = setInterval(() => {
      scheduleBackgroundRefresh();
    }, 30 * 60 * 1000);

    // Schedule cache cleanup (every 2 hours)
    const cleanupInterval = setInterval(() => {
      cleanupOldCache();
    }, 2 * 60 * 60 * 1000);

    return () => {
      clearTimeout(initTimer);
      if (backgroundRefreshInterval.current) {
        clearInterval(backgroundRefreshInterval.current);
      }
      clearInterval(cleanupInterval);
    };
  }, [preloadCriticalData, warmCache, scheduleBackgroundRefresh, cleanupOldCache]);

  return {
    trackAccess,
    scheduleBackgroundRefresh,
    preloadCriticalData,
    warmCache,
    cleanupOldCache
  };
};

export default useCacheOptimization;
